"use client";

import { useRef } from "react";

import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

type Props = {
    value: string;
    onChange: (value: string) => void;
    /**
     * 欄から離れたときに呼ぶ。**手元の下書きを確定させる契機**として使う。
     *
     * 打鍵ごとにサーバーへ書くと、値を空にできない（規定へ戻る）・末尾の空白が落ちる・要求が
     * 打った文字数だけ飛ぶ、が同時に起きる（記録 43）。下書きは呼び出し側が持ち、ここで確定する。
     */
    onBlur?: () => void;
    /**
     * 欄そのものへの参照。**カーソル位置を読む・動かすために開ける。**
     *
     * メンションの候補は「カーソルの直前の `@`」から出す必要がある（記録 23）。本文の最後の
     * `@` を見ていたため、文の途中や先頭に `@` を打っても候補が出なかった。挿入したあとに
     * カーソンをメンションの直後へ戻すのにも要る。
     */
    inputRef?: { current: HTMLTextAreaElement | null };
    placeholder?: string;
    rows?: number;
    disabled?: boolean;
    id?: string;
    /**
     * 利用者が縦の高さを変えられるようにする。既定は不可（自動伸長に任せる）。
     *
     * 長い本文を編集する欄で、好きな高さに固定したい場面のために開ける。つかんで変えた時点で
     * その欄の自動伸長はやめ、以後は利用者が決めた高さを保つ（両方が高さを書くと、打つたびに
     * 戻ってつまみが効かない）。縮められた分は欄の中でスクロールする。
     *
     * つかむ所はブラウザーの右下のつまみではなく、**欄の下辺いっぱいに敷いた帯**にする。
     * 素のつまみは約 15px の四角で、大きさをブラウザーが決めており広げる手段が無い。
     * 帯なら幅ぶんが的になるので狙わずに届く。帯を両押しすると自動伸長へ戻る。
     */
    resizable?: boolean;
};

/**
 * 欄と影の箱が共有する箱の形。**高さの一致条件**なので、片方だけ変えてはいけない。
 * 幅・枠の太さ・余白・字の大きさと行送りが揃っていないと、測った高さがずれて下が切れる。
 */
const BOX = "w-full rounded border px-3 py-2 text-sm";

/**
 * ②の長文入力。中身の長さに合わせて伸び、欄の中ではスクロールしない。
 * 定義は docs/notes/設計ノート/2026-08-27 設計ノート 長文入力欄の自動伸長.md が所有する。
 *
 * 高さは、同じ箱の形を着せた見えない div (影の箱) へ値を写して測る。欄そのものの高さを
 * 空にして測る形だと、縮んだ拍子にペインのスクロールバーが消えて幅が広がり、折り返しが
 * 変わって測り値が足りなくなる。影の箱なら欄の高さを一度も動かさないので、それが起きない。
 *
 * 測った値は state に持たず style.height へ直に書く。Webコンソール開発規則が useEffect の
 * 副作用内での state 更新を禁じているため。結果として再描画も起きない。
 *
 * rows は最小の高さとして残す。属性も残すので、サーバーで描かれる時点の高さも rows になる。
 */
export function Textarea({
    value,
    onChange,
    placeholder,
    rows = 4,
    disabled,
    id,
    resizable,
    onBlur,
    inputRef,
}: Props) {
    const fieldRef = useRef<HTMLTextAreaElement | null>(null);
    const shadowRef = useRef<HTMLDivElement>(null);
    const gripRef = useRef<HTMLDivElement>(null);
    /** 利用者が帯で高さを決めたか。決めたら自動伸長をやめ、その高さを保つ。 */
    const manualRef = useRef(false);

    useIsomorphicLayoutEffect(() => {
        const field = fieldRef.current;
        const shadow = shadowRef.current;
        if (!field || !shadow) return undefined;

        // 下限は rows ぶん。1行と2行を測って行送りを出す (line-height の宣言値に依存しない)。
        const minHeight = (): number => {
            shadow.textContent = "あ\n";
            const oneLine = shadow.offsetHeight;
            shadow.textContent = "あ\nあ\n";
            const twoLines = shadow.offsetHeight;
            return oneLine + (rows - 1) * (twoLines - oneLine);
        };

        const fit = (): void => {
            // 利用者が決めた高さは上書きしない。
            if (manualRef.current) return;
            // 隠されているあいだは測れない (高さが 0 になる) ので、表示されるまで何もしない。
            if (field.offsetParent === null) return;
            const min = minHeight();
            // pre-wrap は末尾の改行で空行を作らないため、1つ足してから測る。
            shadow.textContent = `${value}\n`;
            const content = shadow.offsetHeight;
            field.style.height = `${Math.max(min, content)}px`;
        };

        fit();

        // 幅が変われば折り返しも変わる。コンソールはパネル幅、アドインはペイン幅が可変。
        const observer =
            typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => fit());
        observer?.observe(field);

        const grip = gripRef.current;
        if (!grip) return () => observer?.disconnect();

        // 帯をつかんだ時点で「利用者が決めた」と分かる。素のつまみのように高さの変化から
        // 推し測る必要がないので、掴んだかどうかを見張るしくみは要らない。
        let fromY = 0;
        let fromHeight = 0;

        const hold = (height: number): void => {
            manualRef.current = true;
            // 縮めた分は欄の中でスクロールさせる (class の overflow より inline が勝つ)。
            field.style.overflowY = "auto";
            field.style.height = `${Math.max(minHeight(), height)}px`;
        };

        const onMove = (e: PointerEvent): void => hold(fromHeight + (e.clientY - fromY));
        const onUp = (): void => grip.removeEventListener("pointermove", onMove);

        const onDown = (e: PointerEvent): void => {
            fromY = e.clientY;
            fromHeight = field.offsetHeight;
            // 捕まえておかないと、速く動かして帯の外へ出た瞬間に追従が切れる。
            grip.setPointerCapture(e.pointerId);
            grip.addEventListener("pointermove", onMove);
            // 帯の上でのなぞりが文字選択にならないようにする。
            e.preventDefault();
        };

        // 両押しで自動伸長へ戻す。縮めたまま長い本文へ差し替えると下が見えなくなるため、
        // 戻す道を残す。inline を消して class の overflow へ返す。
        const onDouble = (): void => {
            manualRef.current = false;
            field.style.overflowY = "";
            fit();
        };

        // 帯は掴む以外の手段でも動かせる必要がある (キーボードだけの利用者)。
        const onKey = (e: KeyboardEvent): void => {
            const step = e.key === "ArrowDown" ? 16 : e.key === "ArrowUp" ? -16 : 0;
            if (step === 0) return;
            hold(field.offsetHeight + step);
            e.preventDefault();
        };

        grip.addEventListener("pointerdown", onDown);
        grip.addEventListener("pointerup", onUp);
        grip.addEventListener("pointercancel", onUp);
        grip.addEventListener("dblclick", onDouble);
        grip.addEventListener("keydown", onKey);

        return () => {
            observer?.disconnect();
            grip.removeEventListener("pointerdown", onDown);
            grip.removeEventListener("pointerup", onUp);
            grip.removeEventListener("pointercancel", onUp);
            grip.removeEventListener("dblclick", onDouble);
            grip.removeEventListener("keydown", onKey);
            grip.removeEventListener("pointermove", onMove);
        };
    }, [value, rows, resizable]);

    // 値が空のあいだは伸びないので、はみ出したプレースホルダーを読む手段として欄の中の
    // スクロールを残す。コンソールのタスクとレビューは規定のプロンプト全文をここへ入れている。
    const overflow = value.length === 0 ? "overflow-y-auto" : "overflow-hidden";

    // block と角の落としは帯を敷くときだけ当てる。素の inline-block のままだと欄と帯のあいだに
    // 行送りぶんの隙間が出る。帯を出さない欄は今までどおりの組みにしておく。
    return (
        <div className="relative">
            <textarea
                ref={(el) => {
                    fieldRef.current = el;
                    if (inputRef) inputRef.current = el;
                }}
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                rows={rows}
                disabled={disabled}
                className={`${BOX} resize-none border-border-input bg-surface text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-interactive disabled:bg-sidebar-bg disabled:text-text-muted ${resizable ? "block rounded-b-none" : ""} ${overflow}`}
            />
            {resizable && (
                /* 欄の下辺いっぱいの帯。欄の下枠をそのまま仕切りにするので上枠は持たない。
                   高さを変えるだけで中身は書き換えないため、読むだけの状態 (disabled) でも
                   使えるようにしておく。長い規定を狭い欄で読むときに要る。 */
                <div
                    ref={gripRef}
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label="欄の高さを変える"
                    tabIndex={0}
                    className="h-2 w-full cursor-ns-resize touch-none rounded-b border-x border-b border-border-input bg-surface hover:bg-sidebar-bg focus:outline-none focus:ring-1 focus:ring-interactive"
                />
            )}
            <div
                ref={shadowRef}
                aria-hidden
                className={`${BOX} invisible pointer-events-none absolute inset-x-0 top-0 h-auto border-transparent break-words whitespace-pre-wrap`}
            />
        </div>
    );
}
